/**
 * @file index.ts
 *
 * @fileoverview Contains job wizard util functions.
 */

import { JobBundle } from '~/jobiqo/models/jobs';
import { Product } from '~/jobiqo/types/types';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import Router from 'next/router';

/**
 * Extracts bundle id from current URL query params.
 * @param {string} path
 *  The current path.
 * @returns {number | null}
 *  Bundle id if it exists.
 */
export function getBundleFromQueryParams(path: string): number | null {
  if (!path.includes('?')) {
    return null;
  }

  const searchParams = new URLSearchParams(path.split('?')[1]);
  const bundleId = searchParams.get('bundle');

  if (!bundleId) {
    return null;
  }

  return !isNaN(parseInt(bundleId)) ? parseInt(bundleId) : null;
}

/**
 * Gets the current bundle based on the querystring path.
 *
 * @param bundles
 *  The list of all bundles for the site.
 * @param querystring
 *  The current URL querystring
 * @returns
 *  The full bundle information of the ?bundle property in the querystring (if any).
 */
export function getCurrentBundle(
  bundles: JobBundle[],
  querystring: any,
): Product {
  return bundles.filter(
    (bundle) => bundle.id === Number(querystring.bundle),
  )[0];
}

/**
 * Helper to handle job order checkout.
 *
 * @param jobId
 *   The job ID.
 * @param querystring
 *   The current URL querystring
 * @param nextStep
 *   The next step of job wizard workflow.
 * @param isLastStep
 *   The boolean indicates if current step is the last one.
 * @param checkout
 *   The checkout mutation.
 * @param flow
 *   The flow type.
 * @param locale
 *   The app locale.
 * @param upgradesToSend
 *   The array of upgrade IDs to add to the job.
 */
export function handleCheckout(
  jobId: number,
  querystring: any,
  nextStep: string,
  isLastStep: boolean,
  checkout: any,
  flow?: JobWizardFlow,
  locale?: string,
  upgradesToSend?: number[],
): Promise<void> {
  // Create the order and get the information that comes back.
  return checkout({
    variables: {
      id: jobId,
      language: locale,
      acceptPublishedJob: flow === JobWizardFlow.Upgrades,
      products: upgradesToSend,
    },
  }).then((checkoutResponse) => {
    // Handle errors.
    if (
      checkoutResponse.errors ||
      checkoutResponse.data?.jobCheckoutOrder?.errors?.length > 0
    ) {
      return;
    }
    if (isLastStep) {
      Router.push('/job-manager');
    } else {
      Router.push({
        pathname: nextStep,
        query: {
          params: [
            jobId,
            checkoutResponse.data?.jobCheckoutOrder?.order?.id,
            !!flow ? flow : {},
          ],
          ...(querystring['bundle'] && { bundle: querystring['bundle'] }),
        },
      });
    }
  });
}
